import React, { FC, useMemo } from 'react'
import styles from '../Facets.module.scss'
import { ChipListFilter } from 'components/SA/Filters'
import { FacetsConnector, FacetsConnectorProps } from 'connectors/Facets'
import { handleScrollToTop } from '../helpers'
import { updateClientSideUrl } from '../helpers/url'
import { BaseFacetProps } from '../types'
import { SelectedFacets } from 'types/Facets'
import Skeleton from 'components/SA/Skeleton'
import useAvoidLayoutShift from 'hooks/useAvoidLayoutShift'

type FacetListChipsProps = FacetsConnectorProps &
  BaseFacetProps & {
    onClose?: () => void
    filtersWhiteList?: ReadonlyArray<string>
    customClearFilterCallback?: () => void
    minSelectedToBeVisible?: number
  }

const FacetListChips: FC<FacetListChipsProps> = ({
  onClose,
  filtersWhiteList,
  baseUrl,
  nextAppRoute,
  allSelectedFacets,
  getHref,
  updateFacetValues,
  customClearFilterCallback,
  minSelectedToBeVisible = 0,
}) => {
  const { isSkeletonVisible } = useAvoidLayoutShift()

  const onClickSelectedFacet = ({ facetName, attributeName }: SelectedFacets) => {
    handleScrollToTop(() => {
      const href = getHref({
        [facetName]: updateFacetValues(facetName, attributeName),
        page: '', // Reset Page
      })
      const newUrlPath = `${baseUrl}${href}`
      updateClientSideUrl(newUrlPath, nextAppRoute)
    })
  }

  const handleOnClose = (facet) => {
    onClickSelectedFacet(facet)
    onClose?.()
  }

  const handleClearFilters = () => {
    handleScrollToTop(() => {
      if (!!customClearFilterCallback) {
        customClearFilterCallback()
        return
      }

      updateClientSideUrl(baseUrl, nextAppRoute)
    })
  }

  const isPageFacet = () => {
    const hasPageNumber = allSelectedFacets.some(({ facetName }) => facetName === 'page')
    const hasHitsPerPage = allSelectedFacets.some(({ facetName }) => facetName === 'hitsPerPage')

    if (hasPageNumber && hasHitsPerPage) {
      return 2
    } else if (hasPageNumber || hasHitsPerPage) {
      return 1
    } else {
      return 0
    }
  }

  const selectedFacetsLength = allSelectedFacets.length - isPageFacet()
  const isClearFilterVisible = selectedFacetsLength > 0

  const facetsList = useMemo(
    () =>
      allSelectedFacets.filter(({ facetName }) =>
        !!filtersWhiteList ? filtersWhiteList.includes(facetName) : true
      ),
    [allSelectedFacets, filtersWhiteList]
  )

  if (facetsList.length <= minSelectedToBeVisible - 1) {
    return null
  }

  return (
    <div className={styles.facetListChipsContainer}>
      {isSkeletonVisible ? (
        Array.from({ length: 3 }, () => <Skeleton width='75px' height='40px' />)
      ) : (
        <ChipListFilter
          facetsList={facetsList}
          onClose={handleOnClose}
          onClearFilter={handleClearFilters}
          isClearFilterVisible={isClearFilterVisible}
        />
      )}
    </div>
  )
}

export default FacetsConnector(FacetListChips)
